﻿@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.yard

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.browser.window
import org.example.project.dashboard.LocalScreenRefreshVersion
import org.example.project.enginekartei.KarteiApi
import org.example.project.enginecardview.layouts.TorCard
import org.example.project.service.InfrastrukturApi
import org.example.project.service.YardApi
import org.example.project.utils.All
import org.example.project.service.ZustandManager
import org.example.project.service.HofService
import org.example.project.utils.LocalizationManager
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.TouchEvent
import org.w3c.dom.events.Event
import org.w3c.dom.events.MouseEvent
import kotlin.math.hypot

private const val TILE_SIZE = 24

private data class HofMapTileRect(
    val x: Int,
    val y: Int,
    val width: Int,
    val height: Int,
    val type: String
)

private fun hofMapTileColor(type: String): String =
    when (type) {
        "HALLE" -> "#d9dee5"
        "WEG" -> "#c62828"
        "HOF" -> "#2f343a"
        "PARKEN" -> "#242a31"
        "VERWALTUNG" -> "#1976d2"
        "PAPIER" -> "#f28c28"
        "TOILETTE" -> "#00a6a6"
        "KANTINE" -> "#8e5bb7"
        "EINFAHRT" -> "#2e7d32"
        "AUSFAHRT" -> "#ad1457"
        else -> "transparent"
    }

private fun normalizedHallReference(value: String): String {
    val parts = value
        .trim()
        .replace(Regex("^Halle\\s+", RegexOption.IGNORE_CASE), "")
        .split(Regex("\\s+"))
        .filter { it.isNotBlank() }
    return when {
        parts.isEmpty() -> ""
        parts.distinct().size == 1 -> parts.first()
        else -> parts.joinToString(" ")
    }
}

private fun hallReference(map: Map<String, String>): String =
    normalizedHallReference(map["reference"].orEmpty()).ifBlank {
        normalizedHallReference(map["name"].orEmpty())
    }

private fun hofMapLabel(map: Map<String, String>, localizeLabels: Boolean): String {
    if (!localizeLabels) {
        return when (map["type"].orEmpty()) {
            "WERKSGELAENDE" -> "Werksgelände"
            "PAPIERAUSGABE" -> "Papierausgabe"
            "PAPIERANNAHME" -> "Papierannahme"
            "PARKBEREICH" -> "Parkbereich"
            "HALLE" -> hallReference(map)
                .takeIf { it.isNotBlank() }
                ?.let { "Halle $it" }
                ?: "Halle"
            "TOILETTEN" -> "Toiletten"
            "KANTINE" -> "Kantine"
            "EINFAHRT" -> "Einfahrt"
            "AUSFAHRT" -> "Ausfahrt"
            else -> map["name"].orEmpty()
        }
    }

    val key = when (map["type"].orEmpty()) {
        "WERKSGELAENDE" -> "driver_map_factory_site"
        "PAPIERAUSGABE" -> "driver_map_document_issuance"
        "PAPIERANNAHME" -> "driver_map_document_reception"
        "PARKBEREICH" -> "driver_map_parking_area"
        "HALLE" -> "driver_map_hall"
        "TOILETTEN" -> "driver_map_toilets"
        "KANTINE" -> "driver_map_canteen"
        "EINFAHRT" -> "driver_map_entrance"
        "AUSFAHRT" -> "driver_map_exit"
        else -> return map["name"].orEmpty()
    }
    val translated = LocalizationManager.get(key).takeUnless { it == "[$key]" }
        ?: map["name"].orEmpty()
    return if (map["type"] == "HALLE") {
        val reference = hallReference(map)
        val base = translated.trim()
        if (reference.isBlank() || base == reference || base.endsWith(" $reference")) {
            base
        } else {
            "$base $reference".trim()
        }
    } else {
        translated
    }
}

@Composable
fun HofMapView(
    standortId: String?,
    firmenId: String? = null,
    highlightTor: Int? = null,
    highlightParkplatz: Int? = null,
    occupiedTorIds: Set<Int> = emptySet(),
    assignedBlinkTorIds: Set<Int> = emptySet(),
    blinkAssignedTors: Boolean = false,
    fitToBounds: Boolean = false,
    fitHeightPx: Int? = null,
    freePanZoom: Boolean = false,
    localizeLabels: Boolean = false
) {
    Style(HofMapStyles)
    var layout by remember { mutableStateOf<All?>(null) }
    var parkplatzStamm by remember { mutableStateOf<List<All>>(emptyList()) }
    var zoom by remember { mutableStateOf(1.0) }
    var initialFitApplied by remember(standortId, firmenId, freePanZoom) { mutableStateOf(false) }
    var offsetX by remember { mutableStateOf(0.0) }
    var offsetY by remember { mutableStateOf(0.0) }
    var dragStartX by remember { mutableStateOf<Double?>(null) }
    var dragStartY by remember { mutableStateOf<Double?>(null) }
    var dragStartOffsetX by remember { mutableStateOf(0.0) }
    var dragStartOffsetY by remember { mutableStateOf(0.0) }
    var pinchStartDistance by remember { mutableStateOf<Double?>(null) }
    var pinchStartZoom by remember { mutableStateOf(1.0) }
    var containerRef by remember { mutableStateOf<HTMLElement?>(null) }
    var containerWidthPx by remember { mutableStateOf(0) }
    var containerHeightPx by remember { mutableStateOf(0) }
    var torStamm by remember { mutableStateOf<List<All>>(emptyList()) }
    var hoveredTorId by remember { mutableStateOf<Int?>(null) }
    var hoveredTorX by remember { mutableStateOf(0) }
    var hoveredTorY by remember { mutableStateOf(0) }
    val yardLiveVersion = LocalScreenRefreshVersion.current

    fun measureContainer() {
        containerRef?.let { element ->
            containerWidthPx = element.clientWidth
            containerHeightPx = element.clientHeight
        }
    }

    DisposableEffect(containerRef, fitToBounds, fitHeightPx, freePanZoom) {
        if (!fitToBounds && !freePanZoom) return@DisposableEffect onDispose {}
        val resizeHandler: (Event) -> Unit = { measureContainer() }
        measureContainer()
        window.setTimeout({ measureContainer() }, 0)
        window.addEventListener("resize", resizeHandler)
        onDispose {
            window.removeEventListener("resize", resizeHandler)
        }
    }

    LaunchedEffect(standortId, firmenId, yardLiveVersion) {

        val sid = standortId
            ?: KarteiApi
                .loadStandorte()
                .firstOrNull()
                ?.id

        sid?.let {
            val loaded = YardApi.loadStandortLayout(it, firmenId.orEmpty())
            layout = loaded
            parkplatzStamm = InfrastrukturApi.loadParkplaetze(firmenId.orEmpty())
                .filter { parkplatz -> parkplatz.standort == sid }
            torStamm = InfrastrukturApi.loadTore(firmenId.orEmpty())
                .filter { tor -> tor.standort == sid }
            zoom = if (fitToBounds || freePanZoom) {
                1.0
            } else {
                loaded?.standortZoom?.takeIf { factor -> factor > 0.0 } ?: 1.0
            }
            offsetX = 0.0
            offsetY = 0.0
            initialFitApplied = false
        }
    }
    Div({
        ref { element ->
            containerRef = element
            onDispose { containerRef = null }
        }
        style {
            width(100.percent)
            height(100.percent)
            overflow(if (fitToBounds || freePanZoom) "hidden" else "auto")
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            property("touch-action", if (freePanZoom) "none" else "pan-x pan-y")
            property("user-select", "none")
        }

        onWheel {
            if (fitToBounds && !freePanZoom) return@onWheel
            it.preventDefault()
            val factor = if (it.deltaY < 0) 1.10 else 0.90
            zoom = (zoom * factor).coerceIn(0.25, 5.0)
        }

        onMouseDown {
            if (!freePanZoom) return@onMouseDown
            val event = it.nativeEvent as? MouseEvent ?: return@onMouseDown
            dragStartX = event.clientX.toDouble()
            dragStartY = event.clientY.toDouble()
            dragStartOffsetX = offsetX
            dragStartOffsetY = offsetY
            it.preventDefault()
        }

        onMouseMove {
            if (!freePanZoom) return@onMouseMove
            val startX = dragStartX ?: return@onMouseMove
            val startY = dragStartY ?: return@onMouseMove
            val event = it.nativeEvent as? MouseEvent ?: return@onMouseMove
            offsetX = dragStartOffsetX + event.clientX.toDouble() - startX
            offsetY = dragStartOffsetY + event.clientY.toDouble() - startY
            it.preventDefault()
        }

        onMouseUp {
            dragStartX = null
            dragStartY = null
        }

        onMouseLeave {
            dragStartX = null
            dragStartY = null
        }

        onTouchStart {
            val event = it.nativeEvent as? TouchEvent ?: return@onTouchStart
            if (fitToBounds && !freePanZoom) return@onTouchStart
            if (event.touches.length == 1 && freePanZoom) {
                val touch = event.touches.item(0) ?: return@onTouchStart
                dragStartX = touch.clientX.toDouble()
                dragStartY = touch.clientY.toDouble()
                dragStartOffsetX = offsetX
                dragStartOffsetY = offsetY
                pinchStartDistance = null
                it.preventDefault()
                return@onTouchStart
            }
            if (event.touches.length < 2) return@onTouchStart
            val first = event.touches.item(0) ?: return@onTouchStart
            val second = event.touches.item(1) ?: return@onTouchStart
            pinchStartDistance = hypot(
                (second.clientX - first.clientX).toDouble(),
                (second.clientY - first.clientY).toDouble()
            )
            pinchStartZoom = zoom
            it.preventDefault()
        }

        onTouchMove {
            val event = it.nativeEvent as? TouchEvent ?: return@onTouchMove
            if (fitToBounds && !freePanZoom) return@onTouchMove
            if (event.touches.length == 1 && freePanZoom) {
                val startX = dragStartX ?: return@onTouchMove
                val startY = dragStartY ?: return@onTouchMove
                val touch = event.touches.item(0) ?: return@onTouchMove
                offsetX = dragStartOffsetX + touch.clientX.toDouble() - startX
                offsetY = dragStartOffsetY + touch.clientY.toDouble() - startY
                it.preventDefault()
                return@onTouchMove
            }
            if (event.touches.length < 2) return@onTouchMove
            val startDistance = pinchStartDistance ?: return@onTouchMove
            val first = event.touches.item(0) ?: return@onTouchMove
            val second = event.touches.item(1) ?: return@onTouchMove
            val currentDistance = hypot(
                (second.clientX - first.clientX).toDouble(),
                (second.clientY - first.clientY).toDouble()
            )
            if (startDistance > 0.0) {
                zoom = (pinchStartZoom * (currentDistance / startDistance)).coerceIn(0.25, 5.0)
            }
            it.preventDefault()
        }

        onTouchEnd {
            pinchStartDistance = null
            dragStartX = null
            dragStartY = null
        }
    }) {
        layout?.let { all ->

            val tiles = all.standortTiles
            val tore = all.standortTore
            val parkplaetze = all.standortParkplaetze
            val outlines = all.standortOutlines
            val labels = all.standortLabels
            val tileCoords = tiles.keys.mapNotNull {
                val p = it.split(",")
                if (p.size == 2) p[0].toInt() to p[1].toInt() else null
            }
            val markerCoords = (tore + parkplaetze).map { (it["x"] ?: 0) to (it["y"] ?: 0) }
            val coords = tileCoords + markerCoords

            val minX = coords.minOfOrNull { it.first } ?: 0
            val maxX = coords.maxOfOrNull { it.first } ?: 0
            val minY = coords.minOfOrNull { it.second } ?: 0
            val maxY = coords.maxOfOrNull { it.second } ?: 0

            val widthCells = maxX - minX + 1
            val heightCells = maxY - minY + 1
            val tileSize = TILE_SIZE
            val canvasId = "hof-map-canvas-${standortId.orEmpty()}-${firmenId.orEmpty()}-${widthCells}x${heightCells}"
            val tileRects = buildList {
                val visited = mutableSetOf<Pair<Int, Int>>()
                for (y in minY..maxY) {
                    for (x in minX..maxX) {
                        val type = tiles["$x,$y"].orEmpty()
                        if (type.isBlank() || (x to y) in visited) continue

                        var rectWidth = 1
                        while (x + rectWidth <= maxX && tiles["${x + rectWidth},$y"].orEmpty() == type && (x + rectWidth to y) !in visited) {
                            rectWidth += 1
                        }

                        var rectHeight = 1
                        while (y + rectHeight <= maxY) {
                            val rowMatches = (0 until rectWidth).all { dx ->
                                tiles["${x + dx},${y + rectHeight}"].orEmpty() == type && (x + dx to y + rectHeight) !in visited
                            }
                            if (!rowMatches) break
                            rectHeight += 1
                        }

                        for (dy in 0 until rectHeight) {
                            for (dx in 0 until rectWidth) {
                                visited += (x + dx) to (y + dy)
                            }
                        }
                        add(HofMapTileRect(x - minX, y - minY, rectWidth, rectHeight, type))
                    }
                }
            }
            val calculatedFitZoom = if (fitToBounds || freePanZoom) {
                val availableWidth = containerWidthPx.takeIf { it > 0 }
                    ?: (window.innerWidth - 470).coerceAtLeast(360)
                val availableHeight = containerHeightPx.takeIf { it > 0 }
                    ?: (fitHeightPx ?: (window.innerHeight * 0.50).toInt()).coerceAtLeast(220)
                val scaleX = availableWidth.toDouble() / (widthCells * tileSize).coerceAtLeast(1)
                val scaleY = availableHeight.toDouble() / (heightCells * tileSize).coerceAtLeast(1)
                minOf(scaleX, scaleY).coerceAtMost(5.0) * 0.99
            } else {
                zoom
            }
            LaunchedEffect(calculatedFitZoom, freePanZoom, fitToBounds, widthCells, heightCells) {
                if ((freePanZoom || fitToBounds) && !initialFitApplied && calculatedFitZoom > 0.0) {
                    zoom = calculatedFitZoom
                    offsetX = 0.0
                    offsetY = 0.0
                    initialFitApplied = true
                }
            }
            val fitZoom = if (freePanZoom) zoom else calculatedFitZoom

            LaunchedEffect(canvasId, tileRects) {
                window.setTimeout({
                    val canvas = document.getElementById(canvasId) as? HTMLCanvasElement ?: return@setTimeout
                    val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D ?: return@setTimeout
                    ctx.asDynamic().imageSmoothingEnabled = false
                    ctx.clearRect(0.0, 0.0, canvas.width.toDouble(), canvas.height.toDouble())
                    tileRects.forEach { rect ->
                        ctx.fillStyle = hofMapTileColor(rect.type)
                        ctx.fillRect(
                            (rect.x * tileSize).toDouble(),
                            (rect.y * tileSize).toDouble(),
                            (rect.width * tileSize).toDouble(),
                            (rect.height * tileSize).toDouble()
                        )
                    }
                }, 0)
            }

            Div({
                style {
                    position(Position.Relative)
                            property("transform", if (freePanZoom) "translate(${offsetX}px, ${offsetY}px) scale($fitZoom)" else "scale($fitZoom)")
                            property("transform-origin", if (fitToBounds || freePanZoom) "center center" else "0 0")
                            if (freePanZoom) {
                        cursor(if (dragStartX != null) "grabbing" else "grab")
                    }
                }
            }) {

                /* GRID */
                Canvas(attrs = {
                    id(canvasId)
                    attr("width", (widthCells * tileSize).toString())
                    attr("height", (heightCells * tileSize).toString())
                    style {
                        display(DisplayStyle.Block)
                        width((widthCells * tileSize).px)
                        height((heightCells * tileSize).px)
                    }
                })
                /* TORE */
                tore.forEachIndexed { idx, tor ->
                    val x = (tor["x"] ?: 0) - minX
                    val y = (tor["y"] ?: 0) - minY

                    val torId = tor["id"] ?: (idx + 1)
                    val zustand = HofService.getTorZustand(torId)
                    val isMine = highlightTor == torId
                    val isOccupied = torId in occupiedTorIds || (
                        zustand > ZustandManager.RESERVIERT &&
                            zustand < ZustandManager.ABGESCHLOSSEN
                        )
                    val isBlinking = isMine || (
                        blinkAssignedTors && (
                            torId in assignedBlinkTorIds ||
                                zustand == ZustandManager.TOR_ZUGEWIESEN
                            )
                        )

                    val color = when {
                        isMine -> Color("#e53935")
                        isOccupied -> Color("#b71c1c")
                        else -> Color("#8e8e8e")
                    }

                    Div({
                        style {
                            position(Position.Absolute)
                            left((x * tileSize).px)
                            top((y * tileSize).px)
                            width(tileSize.px)
                            height(tileSize.px)

                            backgroundColor(color)

                            border(2.px, LineStyle.Solid, Color.black)
                            if (isBlinking) {
                                property("animation", "${HofMapStyles.torBlink.name} 850ms ease-in-out infinite")
                                property("box-shadow", "0 0 0 4px rgba(229,57,53,0.34), 0 0 20px rgba(229,57,53,0.78)")
                            } else if (isOccupied) {
                                property("box-shadow", "0 0 0 3px rgba(183,28,28,0.30), 0 0 14px rgba(183,28,28,0.52)")
                            }
                            color(Color.white)
                            fontSize(10.px)
                            fontWeight("bold")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            property("z-index", "2")
                        }
                        onMouseEnter {
                            if (HofService.getBelegung(torId) != null) {
                                hoveredTorId = torId
                                val event = it.nativeEvent as? MouseEvent
                                if (event != null) {
                                    hoveredTorX = event.clientX
                                    hoveredTorY = event.clientY
                                }
                            }
                        }
                        onMouseMove {
                            if (hoveredTorId == torId) {
                                val event = it.nativeEvent as? MouseEvent
                                if (event != null) {
                                    hoveredTorX = event.clientX
                                    hoveredTorY = event.clientY
                                }
                            }
                        }
                        onMouseLeave { hoveredTorId = null }
                    }) {
                        Text("T$torId")
                    }
                }

                /* PARKPLÄTZE */
                parkplaetze.forEachIndexed { idx, parkplatz ->
                    val x = (parkplatz["x"] ?: 0) - minX
                    val y = (parkplatz["y"] ?: 0) - minY
                    val parkplatzId = parkplatz["id"] ?: (idx + 1)
                    val stamm = parkplatzStamm.firstOrNull { it.id.toIntOrNull() == parkplatzId }
                    val isHighlighted = highlightParkplatz == parkplatzId
                    val isBelegt = stamm?.belegt == true
                    val isGesperrt = stamm?.gesperrt == true
                    val color = when {
                        isBelegt -> Color("#b71c1c")
                        isGesperrt -> Color("#8e8e8e")
                        else -> Color("#8e8e8e")
                    }

                    Div({
                        style {
                            position(Position.Absolute)
                            left((x * tileSize).px)
                            top((y * tileSize).px)
                            width(tileSize.px)
                            height(tileSize.px)

                            backgroundColor(color)

                            border(2.px, LineStyle.Solid, Color.black)
                            if (isHighlighted) {
                                property("animation", "${HofMapStyles.torBlink.name} 850ms ease-in-out infinite")
                                property("box-shadow", "0 0 0 4px rgba(102,187,106,0.34), 0 0 20px rgba(102,187,106,0.78)")
                            } else if (isBelegt) {
                                property("box-shadow", "0 0 0 3px rgba(183,28,28,0.30), 0 0 14px rgba(183,28,28,0.52)")
                            }
                            color(Color.white)
                            fontSize(10.px)
                            fontWeight("bold")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                        }
                    }) {
                        Text("P$parkplatzId")
                    }
                }


                /* LABELS */
                labels.distinctBy { map ->
                    listOf(
                        map["type"].orEmpty(),
                        if (map["type"] == "HALLE") hallReference(map)
                        else map["reference"].orEmpty(),
                        map["cells"].orEmpty()
                    ).joinToString("|")
                }.forEach { map ->
                    val name = hofMapLabel(map, localizeLabels)
                    val cells = map["cells"]?.split(";")
                        ?.mapNotNull {
                            val p = it.split(",")
                            if (p.size == 2) p[0].toInt() to p[1].toInt() else null
                        } ?: emptyList()

                    if (cells.isNotEmpty()) {
                        val cx = cells.map { it.first }.average() - minX
                        val cy = cells.map { it.second }.average() - minY
                        Div({
                            style {
                                position(Position.Absolute)
                                left((cx * tileSize).px)
                                top((cy * tileSize).px)
                                backgroundColor(Color("rgba(0,0,0,0.65)"))
                                color(Color.white)
                                padding(4.px)
                                borderRadius(4.px)
                                fontSize(12.px)
                            }
                        }) { Text(name) }
                    }
                }

                /* OUTLINES */
                outlines.forEach { list ->
                    val cells = list.mapNotNull {
                        val p = it.split(",")
                        if (p.size == 2) p[0].toInt() to p[1].toInt() else null
                    }.toSet()

                    cells.forEach { cell ->
                        val x = cell.first - minX
                        val y = cell.second - minY
                        fun has(px: Int, py: Int) =
                            cells.contains((px + minX) to (py + minY))

                        @Composable
                        fun edge(side: String) {
                            Div({
                                style {
                                    position(Position.Absolute)
                                    left((x * tileSize).px)
                                    top((y * tileSize).px)
                                    width(tileSize.px)
                                    height(tileSize.px)
                                    when (side) {
                                        "top" -> property("border-top", "2px solid black")
                                        "bottom" -> property("border-bottom", "2px solid black")
                                        "left" -> property("border-left", "2px solid black")
                                        "right" -> property("border-right", "2px solid black")
                                    }
                                }
                            })
                        }

                        if (!has(x, y - 1)) edge("top")
                        if (!has(x, y + 1)) edge("bottom")
                        if (!has(x - 1, y)) edge("left")
                        if (!has(x + 1, y)) edge("right")
                    }
                }
            }
            val hoverTorId = hoveredTorId
            val hoveredBelegung = hoverTorId?.let { HofService.getBelegung(it) }
            if (hoverTorId != null && hoveredBelegung != null) {
                val stamm = torStamm.firstOrNull { it.tor == hoverTorId }
                    ?: All(
                        standort = standortId.orEmpty(),
                        tor = hoverTorId,
                        torcode = "",
                        halle = ""
                    )
                Div({
                    style {
                        position(Position.Fixed)
                        left((hoveredTorX + 18).px)
                        top((hoveredTorY + 18).px)
                        width(360.px)
                        height(405.px)
                        property("z-index", "999999")
                        property("pointer-events", "none")
                    }
                }) {
                    TorCard(
                        tor = stamm,
                        belegungen = listOf(hoveredBelegung),
                        onTorTextSave = {}
                    )
                }
            }
        }
    }
}

private object HofMapStyles : StyleSheet() {
    val torBlink by keyframes {
        from {
            opacity(1)
            property("filter", "brightness(1.0)")
        }
        50.percent {
            opacity(0.42)
            property("filter", "brightness(1.9)")
        }
        to {
            opacity(1)
            property("filter", "brightness(1.0)")
        }
    }
}

